import * as jsPdf from 'jspdf';
import * as moment from 'moment-timezone';
import { IExternalStorage, ITest } from "src/Interfaces/TestInterface";
import { IWell } from "src/Interfaces/WellInterface";
import { equalGrid, equalLinesHorizonal, equalLinesVertical, mudPropertyHeaderShading } from '../MudReport/DrawReportMud';
import { fillEmptyRows } from "../MudReport/PopDataMud";
import { textArrayInBoxes } from "../PopulateDataMethods";
import * as MM from './SpecMilsVolumeSnapshot';

export const createVolumeSnapshotReport = (test: ITest, well: IWell, logoBlob: HTMLImageElement, volumeReport: jsPdf) => {
    volumeReport.setFillColor(MM.HEADER_COLOR);
    popReport(test, well, logoBlob, volumeReport);
};

const popReport = (test: ITest, well: IWell, logoBlob: HTMLImageElement, volumeReport: jsPdf) => {

    drawHeader(test, well, logoBlob, volumeReport);
    if (test.isVolumeAccountingReport && (test.mudProperty === 'Oil Based Mud' || test.volumeAccounting.trackWaterBasedVolume)) {
        drawDataTrackingVolume(test, volumeReport);
    } else {
        drawDataNonVolume(test, volumeReport);
    }
    drawExternalTotals(test, volumeReport);
    volumeReport.addPage();
    drawHeader(test, well, logoBlob, volumeReport);
    let count = drawExternalStorage(test, 0, volumeReport);
    while (count < test.externalStorage.length) {
        volumeReport.addPage();
        drawHeader(test, well, logoBlob, volumeReport);
        count = drawExternalStorage(test, count, volumeReport);
    }
};

const drawHeader = (test: ITest, well: IWell, logoBlob: HTMLImageElement, volumeReport: jsPdf) => {
    volumeReport.addImage(logoBlob, 'PNG', MM.LOGO_LEFT, MM.LOGO_TOP, MM.LOGO_WIDTH, MM.LOGO_HEIGHT);
    popOperatorContractorDepth(test, well, volumeReport);
};

const popOperatorContractorDepth = (test: ITest, well: IWell, volumeReport: jsPdf) => {
    const boldText = [
        [`Operator: ${well.operator.operatorName}`, `Contractor: ${well.contractor.contractorName}`],
    ];
    fillEmptyRows(boldText, 6);
    const legal = well.state === 'TX'
        ? 'Section ' + well.shlSection + ', Block ' + well.shlBlock + well.shlBlockDirection + ', Township ' + well.shlTownship + well.shlTownshipDirection
        : 'Section ' + well.shlSection + ', Range ' + well.shlRange + well.shlRangeDirection + ', Township ' + well.shlTownship + well.shlTownshipDirection;
    const normalText = [
        [undefined],
        [`Well Name: ${well.wellName}`, `Rig #: ${well.rigNumber}`],
        [`API: ${well.apiNumber}`, `Spud Date: ${well.spudDate}`],
        [`Legal: ${legal}`, `Test Date: ${moment.unix(test.testDate).tz(well.timeZoneName).format("MMM DD YYYY, h:mm a")}`],
        [`County: ${well.countyParish}`, undefined],
        [`State: ${well.state}`, undefined]
    ];
    volumeReport.setFontSize(MM.HEADER_1_SIZE);
    volumeReport.setFontStyle('bold');
    textArrayInBoxes(boldText, MM.OPERATOR_CONTRACTOR_LEFT, MM.OPERATOR_CONTRACTOR_TOP, MM.OPERATOR_CONTRACTOR_WIDTH, MM.OPERTAOR_CONTRACTOR_HEIGHT, 'left', volumeReport);
    volumeReport.setFontStyle('normal');
    textArrayInBoxes(normalText, MM.OPERATOR_CONTRACTOR_LEFT, MM.OPERATOR_CONTRACTOR_TOP, MM.OPERATOR_CONTRACTOR_WIDTH, MM.OPERTAOR_CONTRACTOR_HEIGHT, 'left', volumeReport);

    const depthArr = [
        [undefined],
        ['MD:', test.depthMD],
        ['TVD:', test.depthTVD],
        [undefined],
    ];

    volumeReport.setFontStyle('bold');
    textArrayInBoxes(
        depthArr,
        MM.DEPTH_LEFT,
        MM.DEPTH_TOP,
        MM.DEPTH_WIDTH,
        MM.DEPTH_HEIGHT,
        'left',
        volumeReport
    );
};

const drawDataTrackingVolume = (test: ITest, volumeReport: jsPdf) => {

    const currInterval = getCurrentInterval(test);
    const va = test.volumeAccounting;
    const columnHeaderArr = [
        ['General Volume (bbls)', 'Base Oil Acctg (gal)', 'Additions (bbls)', 'Losses (bbls)', 'Reconciliation Volume', 'Final Volume'],
    ];

    fillEmptyRows(columnHeaderArr, MM.DATA_ROWS);
    const dataHeaderArr = [
        [undefined],
        ['New Hole Volume', 'Starting Base Oil', 'Base Oil Used', 'Loss to Surface', 'Previous Test Volume', 'Total Hole Volume'],
        [undefined],
        ['New Footage Drilled', 'Base Oil Received', 'Water', 'Loss to Formation', 'Total Vol Additions', 'PVT'],
        [undefined],
        ['Displacement Depth (MD)', 'Ending Base Oil', 'Chemical Additions', 'Solids Control', 'New Hole Volume Built', 'External Storage'],
        [undefined],
        ['Other Vol Received', 'Base Oil Used', 'Slurry', 'Loss to Cuttings', 'Total Vol Losses', 'Volume on Location'],
        [undefined],
        ['Other Vol Transferred', 'Cum Base Oil Used FLUID: ' + test.propertyType, 'Water Flow', 'Mud Left in Hole', 'New Hole Volume Deducted', undefined],
        [undefined],
        ['Plant Vol Received', 'Cum Base Oil Used INTERVAL: ' + (currInterval !== undefined ? currInterval.intervalType : ''), 'Other Additions', 'Other Losses', 'Reconciliation Factor', undefined ],
        [undefined],
        ['Plant Vol Returned', 'Cum Base Oil Used TOTAL: Well', 'Total Vol Additions', 'Total Vol Losses', undefined, undefined],
        [undefined],
        ['Vol Owed to Plant', 'Lost to Cutting Ratio', ...Array(3), 'Required Reconciliation'],
        [undefined],
    ];

    const reconFactor = va.previousTotalHoleVolume + va.totalAdditions - va.totalLosses;
    const currIntCumBaseOil = currInterval !== undefined ? currInterval.cumBaseOilUsed + va.baseOilUsed : va.baseOilUsed;
    const dataArr = [
        [undefined],
        [undefined],
        [va.newHoleVolume, va.startingBaseOil, va.baseOil, va.lossToSurface, va.previousTotalHoleVolume, va.totalHoleVolume],
        [undefined],
        [va.footageDrilled, va.baseOilReceived, va.water, va.lossToFormation, va.totalAdditions, test.pumpData.pitVolume],
        [undefined],
        [va.displacement, va.endingBaseOil, va.chemicalAdditions, va.solidsControl, va.newHoleVolume, va.externalStorage],
        [undefined],
        [va.otherReceived, va.baseOilUsed, va.slurry, va.lossToCuttings, va.totalLosses, va.totalVolume ],
        [undefined],
        [va.otherTransfer, va.baseOilUsed + getPropertyTypeCumBaseOil(test), va.washout, va.mudLeftInHole, va.newHoleVolume, undefined],
        [undefined],
        [va.plantVolRecvd, currIntCumBaseOil, va.otherAdditions, va.otherLoss, reconFactor, undefined],
        [undefined],
        [va.plantVolReturned, va.cumBaseOilUsed, va.totalAdditions, va.totalLosses, undefined, undefined],
        [undefined],
        [va.volOwedToPlant, va.lostToCuttingsRatio + ' : 1', undefined, undefined, undefined, va.requiredReconciliation ],
    ];
    volumeReport.setFillColor(MM.HEADER_COLOR);
    mudPropertyHeaderShading(MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, MM.DATA_ROWS, dataShading, volumeReport);
    equalGrid(MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, MM.DATA_ROWS, MM.DATA_COLUMNS_TRACKING, volumeReport);
    volumeReport.rect(MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT);
    volumeReport.setFontStyle('bold');
    volumeReport.setFontSize(12);
    textArrayInBoxes(columnHeaderArr, MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, 'center', volumeReport);
    volumeReport.setFontSize(9);
    textArrayInBoxes(dataHeaderArr, MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, 'left', volumeReport);
    volumeReport.setFontStyle('normal');
    textArrayInBoxes(dataArr, MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, 'center', volumeReport);
};

const drawDataNonVolume = (test: ITest, volumeReport: jsPdf) => {

    const currInterval = getCurrentInterval(test);
    const va = test.volumeAccounting;
    const columnHeaderArr = [
        ['Base Oil Acctg (gal)', 'Additions (bbls)', 'Losses (bbls)'],
    ];

    fillEmptyRows(columnHeaderArr, MM.DATA_ROWS);
    const dataHeaderArr = [
        [undefined],
        ['Starting Base Oil', 'Base Oil Used', 'Loss to Surface'],
        [undefined],
        ['Base Oil Received', 'Water', 'Loss to Formation'],
        [undefined],
        ['Ending Base Oil', 'Chemical Additions', 'Solids Control'],
        [undefined],
        ['Base Oil Used', 'Slurry', 'Loss to Cuttings'],
        [undefined],
        ['Cum Base Oil Used FLUID: ' + test.propertyType, 'Water Flow', 'Mud Left in Hole'],
        [undefined],
        ['Cum Base Oil Used INTERVAL: ' + (currInterval !== undefined ? currInterval.intervalType : ''), 'Other Additions', 'Other Losses'],
        [undefined],
        ['Cum Base Oil Used TOTAL: Well', 'Total Vol Additions', 'Total Vol Losses'],
        [undefined],
        ['Lost to Cutting Ratio', ...Array(2)],
        [undefined],
    ];

    const currIntCumBaseOil = currInterval !== undefined ? currInterval.cumBaseOilUsed + va.baseOilUsed : va.baseOilUsed;
    const dataArr = [
        [undefined],
        [undefined],
        [va.startingBaseOil, va.baseOil, va.lossToSurface],
        [undefined],
        [va.baseOilReceived, va.water, va.lossToFormation],
        [undefined],
        [va.endingBaseOil, va.chemicalAdditions, va.solidsControl],
        [undefined],
        [va.baseOilUsed, va.slurry, va.lossToCuttings],
        [undefined],
        [va.baseOilUsed + getPropertyTypeCumBaseOil(test), va.washout, va.mudLeftInHole],
        [undefined],
        [currIntCumBaseOil, va.otherAdditions, va.otherLoss],
        [undefined],
        [va.cumBaseOilUsed, va.totalAdditions, va.totalLosses],
        [undefined],
        [va.lostToCuttingsRatio + ' : 1', undefined, undefined],
    ];
    volumeReport.setFillColor(MM.HEADER_COLOR);
    mudPropertyHeaderShading(MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, MM.DATA_ROWS, dataShading, volumeReport);
    equalGrid(MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, MM.DATA_ROWS, MM.DATA_COLUMNS_NON, volumeReport);
    volumeReport.rect(MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT);
    volumeReport.setFontStyle('bold');
    volumeReport.setFontSize(12);
    textArrayInBoxes(columnHeaderArr, MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, 'center', volumeReport);
    volumeReport.setFontSize(9);
    textArrayInBoxes(dataHeaderArr, MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, 'left', volumeReport);
    volumeReport.setFontStyle('normal');
    textArrayInBoxes(dataArr, MM.PAGE_BORDER_WIDTH, MM.DATA_TOP, MM.DATA_WIDTH, MM.DATA_HEIGHT, 'center', volumeReport);

};

const drawExternalStorage = (test: ITest, startIndex: number, volumeReport: jsPdf) => {
    const externalHeaders = [
        ['Number', 'Storage Type', 'Fluid Type', 'Fluid Sub Type', 'Prev Fluid Amt', 'Today Fluid Amt', 'Transferred In', 'Transferred Out', 'Capacity', 'Mud Weight', 'Description', 'In Use'],
    ];

    const externalData = [
        [undefined],
    ] as any;
    const esArray = test.externalStorage;
    let i = startIndex;
    for (; i < esArray.length && i - startIndex < MM.MAX_STORAGE_ROWS; i++) {
        const es = esArray[i];
        const tempRow = [
            es.number,
            es.storageType,
            es.fluidType,
            es.fluidSubType,
            es.previousFluidAmount,
            es.todayFluidAmount,
            es.xferIn,
            es.xferOut,
            es.capacity,
            es.mudWeight,
            es.other,
            es.inUse ? 'Yes' : 'No',
        ];
        externalData.push(tempRow);
    }

    const tableHeight = externalData.length * MM.EXTERNAL_TABLE_ROW_HEIGHT;
    volumeReport.setFillColor(MM.HEADER_COLOR);
    volumeReport.rect(MM.DATA_LEFT, MM.DATA_TOP, MM.DATA_WIDTH, MM.EXTERNAL_TABLE_ROW_HEIGHT, 'f');
    fillEmptyRows(externalHeaders, externalData.length);
    volumeReport.setFontStyle('bold');
    textArrayInBoxes(externalHeaders, MM.DATA_LEFT, MM.DATA_TOP, MM.DATA_WIDTH, tableHeight, 'center', volumeReport);
    volumeReport.setFontStyle('normal');
    textArrayInBoxes(externalData, MM.DATA_LEFT, MM.DATA_TOP, MM.DATA_WIDTH, tableHeight, 'center', volumeReport);
    equalGrid(MM.DATA_LEFT, MM.DATA_TOP, MM.DATA_WIDTH, tableHeight, externalData.length, MM.EXTERNAL_TABLE_COLUMNS, volumeReport);
    volumeReport.rect(MM.DATA_LEFT, MM.DATA_TOP, MM.DATA_WIDTH, tableHeight);

    return i;
};

const drawExternalTotals = (test: ITest, volumeReport: jsPdf) => {
    let oilTotal: number = 0;
    let waterTotal: number = 0;
    const fluidSubTypeOptionsOil: IFluidSubTypeTotal[] = [];
    const fluidSubTypeOptionsWater: IFluidSubTypeTotal[] = [];

    if (test.externalStorage.length > 0) {
        test.externalStorage.map((item: IExternalStorage, index: number) => {
            if (item.inUse) {
                if (item.fluidType === 'OBM') {
                    oilTotal += Number(item.todayFluidAmount);
                    if (item.fluidSubType !== '') {

                        let foundSubType = false;
                        for (const element of fluidSubTypeOptionsOil) {
                            if (item.fluidSubType === element.subType) {
                                element.total += Number(item.todayFluidAmount);
                                foundSubType = true;
                                break;
                            }
                        }
                        
                        if (!foundSubType) {
                            fluidSubTypeOptionsOil.push({
                                subType: item.fluidSubType,
                                total: Number(item.todayFluidAmount),
                            });
                        }
                    }
                } else {
                    waterTotal += Number(item.todayFluidAmount);
                    if (item.fluidSubType !== '') {

                        let foundSubType = false;
                        for (const element of fluidSubTypeOptionsWater) {
                            if (item.fluidSubType === element.subType) {
                                element.total += Number(item.todayFluidAmount);
                                foundSubType = true;
                                break;
                            }
                        }
                        
                        if (!foundSubType) {
                            fluidSubTypeOptionsWater.push({
                                subType: item.fluidSubType,
                                total: Number(item.todayFluidAmount),
                            });
                        }
                    }
                }
            }
        });
    }
    
    const headerDataArr = [
        ['Water Total:', 'Oil Total:'],
        [undefined],
        [undefined],
        [undefined],
    ];
    const waterLength = fluidSubTypeOptionsWater.length;
    const waterTypes = fluidSubTypeOptionsWater.map(fsto => fsto.subType);
    const waterHeaderArr = [
        [undefined],
        [undefined],
        [...waterTypes, ...Array(waterLength)],
        [undefined],
    ];

    const oilLength = fluidSubTypeOptionsOil.length;
    const oilTypes = fluidSubTypeOptionsOil.map(fsto => fsto.subType);
    const oilHeaderArr = [
        [undefined],
        [undefined],
        [ ...Array(oilLength), ...oilTypes],
        [undefined],
    ];

    
    const waterData = fluidSubTypeOptionsWater.map(fsto => fsto.total);
    const oilData = fluidSubTypeOptionsOil.map(fsto => fsto.total);

    const waterDataArr = [
        [undefined],
        [waterTotal, undefined],
        [undefined],
        [...waterData, ...Array(waterLength)],
    ];

    const oilDataArr = [
        [undefined],
        [undefined, oilTotal],
        [undefined],
        [...Array(oilLength), ...oilData],
    ];
    volumeReport.setFillColor(MM.HEADER_COLOR);
    mudPropertyHeaderShading(MM.PAGE_BORDER_WIDTH, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, MM.EXTERNAL_TOTAL_ROWS, externalTotalShading, volumeReport);
    equalLinesHorizonal(MM.PAGE_BORDER_WIDTH, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, MM.EXTERNAL_TOTAL_ROWS, volumeReport);
    equalLinesVertical(MM.PAGE_BORDER_WIDTH, MM.EXTERNAL_TOP + MM.EXTERNAL_HEIGHT / 2, MM.EXTERNAL_WIDTH / 2, MM.EXTERNAL_HEIGHT / 2, waterLength, volumeReport);
    equalLinesVertical(MM.PAGE_BORDER_WIDTH + MM.EXTERNAL_WIDTH / 2, MM.EXTERNAL_TOP + MM.EXTERNAL_HEIGHT / 2, MM.EXTERNAL_WIDTH / 2, MM.EXTERNAL_HEIGHT / 2, oilLength, volumeReport);
    volumeReport.rect(MM.PAGE_BORDER_WIDTH, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH / 2, MM.EXTERNAL_HEIGHT);
    volumeReport.rect(MM.PAGE_BORDER_WIDTH + MM.EXTERNAL_WIDTH / 2, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH / 2, MM.EXTERNAL_HEIGHT);
    volumeReport.setFontStyle('bold');
    textArrayInBoxes(headerDataArr, MM.PAGE_BORDER_WIDTH, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, 'center', volumeReport);
    textArrayInBoxes(waterHeaderArr, MM.PAGE_BORDER_WIDTH, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, 'center', volumeReport);
    textArrayInBoxes(oilHeaderArr, MM.PAGE_BORDER_WIDTH, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, 'center', volumeReport);
    volumeReport.setFontStyle('normal');
    textArrayInBoxes(waterDataArr, MM.PAGE_BORDER_WIDTH, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, 'center', volumeReport);
    textArrayInBoxes(oilDataArr, MM.PAGE_BORDER_WIDTH, MM.EXTERNAL_TOP, MM.EXTERNAL_WIDTH, MM.EXTERNAL_HEIGHT, 'center', volumeReport);
};

interface IFluidSubTypeTotal{
    subType: string;
    total: number;
}

const getCurrentInterval = (test: ITest) => {
    const intervals = test.intervals;
    if (intervals && intervals.length > 0 && intervals[0]) {
        return intervals[0];
    }
    return undefined;
};

const getPropertyTypeCumBaseOil = (test: ITest) => {
    for (const propertyUsed of test.volumeAccounting.propertyTypeCumBaseOilUseds) {
        if (propertyUsed.propertyType === test.propertyType) {
            return propertyUsed.cumBaseOilUsed;
        }
    }

    return 0;
};

const dataShading = [
    1,
    1,
    0,
    1,
    0,
    1,
    0,
    1,
    0,
    1,
    0,
    1,
    0,
    1,
    0,
    1,
    0,
];

const externalTotalShading = [
    1,
    0,
    1,
    0,
];



// WEBPACK FOOTER //
// ./src/Services/Pdf/VolumeAccountingSnapshotReport/DrawReportVolumeSnapshot.ts